import { CommerceDevicePermissions, ExpoPermissionStatus } from '@gooduncles/gu-app-schema';
import { Card, Empty, Tag } from 'antd';
import { FC, ReactNode } from 'react';

import { getCommerceDevicesByUserId } from 'src/utils/firebase/firebase-short-cut';
import { formatDate } from 'src/utils/date-util';

import Loading from 'src/components/Loading/Loading';

import classes from './UserDevice.module.scss';

import useSWR from 'swr';

type UserDeviceProps = {
  userId: string;
};

const permissionLabelKr: Record<keyof CommerceDevicePermissions, string> = {
  notification: '알림',
  camera: '카메라',
  photo: '사진',
};

const permissionStatusKr: Record<ExpoPermissionStatus, string> = {
  granted: '허용',
  denied: '거부',
  undetermined: '미결정',
};

const permissionStatusColor: Record<ExpoPermissionStatus, string> = {
  granted: 'green',
  denied: 'red',
  undetermined: 'default',
};

const platformLabel: Record<'ios' | 'android', string> = {
  ios: 'iOS',
  android: 'Android',
};

const formatDateTime = (value: string | null | undefined): string =>
  value ? formatDate(value, 'yyyy-MM-dd HH:mm:ss') : '-';

type RowProps = {
  label: string;
  field?: string;
  children: ReactNode;
};

const Row: FC<RowProps> = ({ label, field, children }) => (
  <div className={classes.flexRow}>
    <div className={classes.label}>
      {label}
      {field && <span className={classes.subLabel}>({field})</span>}
    </div>
    <div className={classes.value}>{children}</div>
  </div>
);

const PermissionTags: FC<{ permissions: CommerceDevicePermissions }> = ({ permissions }) => (
  <div className={classes.permissionTags}>
    {(Object.keys(permissionLabelKr) as (keyof CommerceDevicePermissions)[]).map((key) => {
      const status = permissions[key];
      return (
        <Tag key={key} color={permissionStatusColor[status]}>
          {permissionLabelKr[key]}: {permissionStatusKr[status] ?? status}
        </Tag>
      );
    })}
  </div>
);

const UserDevice: FC<UserDeviceProps> = ({ userId }) => {
  const {
    data: devices,
    error,
    isValidating,
  } = useSWR(userId ? [userId, 'commerce-devices'] : null, () => getCommerceDevicesByUserId(userId), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  if (isValidating && !devices) {
    return <Loading title='기기 정보를 불러오는 중입니다.' />;
  }

  if (error) {
    return <div className={classes.userDeviceContainer}>기기 정보를 불러오지 못했습니다.</div>;
  }

  if (!devices || devices.length === 0) {
    return (
      <div className={classes.userDeviceContainer}>
        <Empty description='등록된 기기가 없습니다.' />
      </div>
    );
  }

  const sortedDevices = [...devices].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));

  return (
    <div className={classes.userDeviceContainer}>
      <p className={classes.summary}>총 {sortedDevices.length}대의 기기가 이 사용자로 로그인했습니다.</p>
      {sortedDevices.map((device) => (
        <Card
          key={device._id}
          size='small'
          className={classes.deviceCard}
          title={
            <div className={classes.cardTitle}>
              <span>
                {platformLabel[device.platform]} {device.platformVersion}
              </span>
              <Tag color={device.active ? 'green' : 'red'}>{device.active ? '활성' : '비활성'}</Tag>
            </div>
          }
          extra={<span className={classes.appVersion}>v{device.appVersion}</span>}
        >
          <Row label='앱 권한' field='permissions'>
            <PermissionTags permissions={device.permissions} />
          </Row>
          <Row label='최근 앱 실행' field='updatedAt'>
            {formatDateTime(device.updatedAt)}
          </Row>
          <Row label='최근 로그인' field='latestSignedInAt'>
            {formatDateTime(device.latestSignedInAt)}
          </Row>
          <Row label='앱 버전' field='appVersion'>
            {device.appVersion}
          </Row>
          <Row label='OTA 업데이트' field='otaUpdate'>
            {device.otaUpdate ? (
              <span>
                {device.otaUpdate.channel} · {formatDateTime(device.otaUpdate.createdAt)}
                <br />
                <span className={classes.mono}>{device.otaUpdate.updateId}</span>
              </span>
            ) : (
              '내장 번들'
            )}
          </Row>
          <Row label='구독 채널' field='subscribedChannelIds'>
            {device.subscribedChannelIds?.length ?? 0}개
          </Row>
          <Row label='기기 고유 ID' field='deviceId'>
            <span className={classes.mono}>{device.deviceId}</span>
          </Row>
          <Row label='Push Token' field='token'>
            <span className={classes.mono}>{device.token}</span>
          </Row>
        </Card>
      ))}
    </div>
  );
};

export default UserDevice;
